/* Atlas V3 Desktop — Agent Desk. The signed-in user's agentic Robinhood
   account, mirrored from Supabase (agent_holdings + agent_decisions). Three
   zones: account header (equity / buying power / synced staleness), holdings
   table, and the decision log below (reverse-chron, collapsible reasoning).

   MIRROR DATA ONLY — no Robinhood calls anywhere. Data from useAgentPortfolio()
   (Clerk-auth'd, per-user; surfaces error). Tap a row to open the ticker dock.
   Rides the DesktopShell chrome like the other desktop pages (no own shell). */
import { useEffect, useRef, useState } from 'react'
import { StockLogo } from '@/v2/components/StockLogo'
import { useTicker } from '@/v2/v3/lib'
import { useTickerPanel } from '../TickerPanelProvider'
import { useAgentPortfolio } from '@/v2/hooks/useAgentPortfolio'
import { useAgentConfig } from '@/v2/hooks/useAgentConfig'
import { AgentAccess } from '@/v2/components/AgentAccess'
import { AgentConfigDialog, GearButton, HaltLine } from '@/v2/components/agentconfig/AgentConfigPanel'

const Mono = { fontFamily: 'var(--studio-font-mono)' }
const Sans = { fontFamily: 'var(--studio-font-sans)' }
const NUM = { ...Mono, fontVariantNumeric: 'tabular-nums' }

function fmtMoney(v, digits = 2) {
  if (v == null || !Number.isFinite(v)) return '—'
  return `$${v.toLocaleString('en-US', { minimumFractionDigits: digits, maximumFractionDigits: digits })}`
}
function fmtSigned(v, digits = 2) {
  if (v == null || !Number.isFinite(v)) return '—'
  const a = Math.abs(v).toLocaleString('en-US', { minimumFractionDigits: digits, maximumFractionDigits: digits })
  return `${v >= 0 ? '+' : '−'}$${a}`
}
function fmtQty(v) {
  if (v == null || !Number.isFinite(v)) return '—'
  return Number.isInteger(v) ? String(v) : v.toLocaleString('en-US', { maximumFractionDigits: 4 })
}
function relTime(iso) {
  if (!iso) return null
  const then = new Date(iso).getTime()
  if (!Number.isFinite(then)) return null
  const secs = Math.round((Date.now() - then) / 1000)
  if (secs < 60) return 'just now'
  const mins = Math.round(secs / 60)
  if (mins < 60) return `${mins}m ago`
  const hrs = Math.round(mins / 60)
  if (hrs < 24) return `${hrs}h ago`
  return `${Math.round(hrs / 24)}d ago`
}
function unrealized(h) {
  if (h.last_price == null || h.avg_cost == null || h.qty == null) return null
  return (h.last_price - h.avg_cost) * h.qty
}
function dirColor(v) {
  return v == null ? '--studio-text-subtle' : v >= 0 ? '--studio-success' : '--studio-danger'
}

// Shared holdings grid — ticker · qty · avg · last · mkt val · P&L. Header +
// rows share it so columns line up. Numbers right-aligned, tabular mono.
const GRID = {
  display: 'grid',
  gridTemplateColumns: '34px minmax(120px, 1.4fr) 90px 90px 100px 110px 110px',
  alignItems: 'center',
  gap: 14,
}
const HDR = { ...Mono, fontSize: 9, letterSpacing: '0.14em', color: 'hsl(var(--studio-text-subtle))', textTransform: 'uppercase' }
const cap = { ...Mono, fontSize: 12, letterSpacing: '0.08em', color: 'hsl(var(--studio-text-subtle))', padding: '48px 8px', textAlign: 'center' }
const rightNum = (col) => ({ ...NUM, fontSize: 13, color: `hsl(var(${col}))`, textAlign: 'right' })

function HoldingRow({ h, onOpen }) {
  const pnl = unrealized(h)
  return (
    <div className="v3web-row" onClick={() => onOpen(h.ticker)} style={GRID}>
      <StockLogo ticker={h.ticker} size={28} rounded="50%" />
      <div style={{ minWidth: 0 }}>
        <div style={{ ...Mono, fontSize: 13.5, color: 'hsl(var(--studio-text))' }}>{h.ticker}</div>
      </div>
      <span style={rightNum('--studio-text-muted')}>×{fmtQty(h.qty)}</span>
      <span style={rightNum('--studio-text-muted')}>{fmtMoney(h.avg_cost)}</span>
      <span style={rightNum('--studio-text')}>{fmtMoney(h.last_price)}</span>
      <span style={rightNum('--studio-text')}>{fmtMoney(h.market_value)}</span>
      <span style={rightNum(dirColor(pnl))}>{fmtSigned(pnl)}</span>
    </div>
  )
}

/* Amber budget: PROPOSED + the conviction bar are the row's only amber; the
   rating folds into the mono meta line (no pill that looks tappable). */
function DecisionRow({ d, onOpen }) {
  const [open, setOpen] = useState(false)
  const cd = d.conviction_delta
  const barH = Math.min(34, 10 + (cd != null ? Math.abs(cd) * 2 : 0))
  const meta = [
    d.atlas_rating ? d.atlas_rating.toUpperCase() : null,
    cd != null && cd !== 0 ? `CONVICTION ${cd >= 0 ? '+' : '−'}${Math.abs(cd)}` : null,
    relTime(d.created_at)?.toUpperCase() || null,
  ].filter(Boolean).join(' · ')
  return (
    <div style={{ position: 'relative', borderTop: '1px solid hsl(var(--studio-border) / 0.4)', padding: '15px 4px 15px 14px' }}>
      <span aria-hidden style={{ position: 'absolute', left: 0, top: 16, width: 2, height: barH, background: 'hsl(var(--studio-accent))' }} />
      <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
        <button onClick={() => onOpen(d.ticker)} style={{ display: 'flex', alignItems: 'center', gap: 9, background: 'transparent', border: 'none', padding: 0, cursor: 'pointer', minWidth: 0 }}>
          <StockLogo ticker={d.ticker} size={24} rounded="50%" />
          <span style={{ ...Mono, fontSize: 13.5, color: 'hsl(var(--studio-text))' }}>{d.ticker}</span>
        </button>
        <span style={{ ...Mono, fontSize: 11, letterSpacing: '0.08em', textTransform: 'uppercase', color: 'hsl(var(--studio-text))' }}>{d.action}</span>
        {d.qty != null && <span style={{ ...NUM, fontSize: 12, color: 'hsl(var(--studio-text-muted))' }}>×{fmtQty(d.qty)}</span>}
        {d.price != null && <span style={{ ...NUM, fontSize: 12, color: 'hsl(var(--studio-text-subtle))' }}>@ {fmtMoney(d.price)}</span>}
        {meta && <span style={{ ...Mono, fontSize: 10, letterSpacing: '0.04em', color: 'hsl(var(--studio-text) / 0.55)' }}>{meta}</span>}
        <span style={{ flex: 1 }} />
        {d.placed
          ? <span style={{ ...Mono, fontSize: 8.5, letterSpacing: '0.1em', color: 'hsl(var(--studio-success))' }}>PLACED</span>
          : <span style={{ ...Mono, fontSize: 8.5, letterSpacing: '0.1em', color: 'hsl(var(--studio-accent))', border: '1px solid hsl(var(--studio-accent) / 0.4)', borderRadius: 'var(--studio-radius-sm)', padding: '2px 6px' }}>PROPOSED</span>}
      </div>
      {d.reasoning && (
        <>
          <div style={{
            ...Sans, fontSize: 13, lineHeight: 1.55, color: 'hsl(var(--studio-text-muted))', marginTop: 9, maxWidth: 680,
            ...(open ? {} : { display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }),
          }}>{d.reasoning}</div>
          {d.reasoning.length > 120 && (
            <button onClick={() => setOpen((o) => !o)} style={{ ...Mono, fontSize: 9, letterSpacing: '0.1em', color: 'hsl(var(--studio-accent-soft))', background: 'transparent', border: 'none', padding: '6px 0 0', cursor: 'pointer' }}>
              {open ? 'LESS —' : 'MORE +'}
            </button>
          )}
        </>
      )}
    </div>
  )
}

// `embedded` — rendered inside the My Stocks cluster tab, which brings its own
// page heading; hide this page's phead so there's a single header.
export function AgentDesk({ embedded = false }) {
  const openTicker = useTicker()
  const { hasTicker } = useTickerPanel()
  const { holdings, decisions, account, syncedAt, loading, error } = useAgentPortfolio()
  const agentCfg = useAgentConfig()
  const [cfgOpen, setCfgOpen] = useState(false)

  // Off-universe row taps (agents can trade anything) flash a visible
  // "NOT COVERED BY ATLAS" instead of the dock's silent no-op.
  const [notCovered, setNotCovered] = useState(null)
  const flashTimer = useRef(null)
  useEffect(() => () => clearTimeout(flashTimer.current), [])
  const tryOpen = (t) => {
    const known = hasTicker(t)
    if (known) { openTicker(t); return }
    if (known === false) {
      setNotCovered(t)
      clearTimeout(flashTimer.current)
      flashTimer.current = setTimeout(() => setNotCovered(null), 2200)
    }
  }

  // Header prefers STORED account state (agent_account_state); position-derived
  // sums are the fallback for accounts synced before account state existed.
  const derivedEquity = holdings.reduce((s, h) => s + (h.market_value ?? (h.last_price != null && h.qty != null ? h.last_price * h.qty : 0) ?? 0), 0)
  const equity = account?.equity ?? (holdings.length ? derivedEquity : null)
  const buyingPower = account?.buying_power ?? null
  const totalPnl = holdings.reduce((s, h) => { const p = unrealized(h); return p == null ? s : s + p }, 0)
  const hasHoldings = holdings.length > 0
  const hasCash = (account?.cash ?? 0) > 0
  const synced = relTime(syncedAt)

  return (
    <div style={{ maxWidth: 900 }}>
      {/* ── zone 1 · account header (own phead only when standalone) ── */}
      {!embedded && (
        <div className="v3web-phead">
          <div className="k">My Agent · Robinhood Mirror</div>
          <div className="h" style={{ fontStyle: 'italic' }}>Agent Desk</div>
        </div>
      )}

      <div style={{ display: 'flex', alignItems: 'flex-end', gap: 40, marginTop: -8, marginBottom: 8 }}>
        <div>
          <div style={HDR}>Equity</div>
          <div style={{ ...NUM, fontSize: 32, color: 'hsl(var(--studio-text))', marginTop: 4 }}>{equity != null ? fmtMoney(equity) : '—'}</div>
        </div>
        <div>
          <div style={HDR}>Buying power</div>
          <div style={{ ...NUM, fontSize: 18, color: `hsl(var(${buyingPower != null ? '--studio-text' : '--studio-text-subtle'}))`, marginTop: 8 }}>{buyingPower != null ? fmtMoney(buyingPower) : '—'}</div>
        </div>
        <div>
          <div style={HDR}>Unrealized P&amp;L</div>
          <div style={{ ...NUM, fontSize: 18, color: `hsl(var(${hasHoldings ? dirColor(totalPnl) : '--studio-text-subtle'}))`, marginTop: 8 }}>{hasHoldings ? fmtSigned(totalPnl) : '—'}</div>
        </div>
        <div style={{ ...Mono, fontSize: 10, letterSpacing: '0.1em', color: 'hsl(var(--studio-text-subtle))', paddingBottom: 4 }}>
          {loading ? 'SYNCING…' : error ? 'SYNC FAILED' : synced ? `SYNCED ${synced.toUpperCase()}` : 'NOT YET SYNCED'}
          {notCovered && <span style={{ color: 'hsl(var(--studio-accent))' }}> · {notCovered} NOT COVERED BY ATLAS</span>}
        </div>
        <div style={{ marginLeft: 'auto', paddingBottom: 2 }}>
          <GearButton onClick={() => setCfgOpen(true)} />
        </div>
      </div>
      {agentCfg.config?.kill_switch && <HaltLine />}

      {/* ── zone 2 · holdings — table (header included) only when rows exist;
          an empty account gets one status strip and the log moves up ── */}
      <div style={{ marginTop: 22 }}>
        {loading ? (
          <div style={cap}>LOADING ACCOUNT…</div>
        ) : error ? (
          <div style={cap}>COULD NOT REACH ACCOUNT — {String(error.message || error).toUpperCase()}</div>
        ) : !hasHoldings ? (
          <div style={{ ...Mono, fontSize: 11, letterSpacing: '0.1em', color: 'hsl(var(--studio-text-muted))', padding: '12px 4px', borderTop: '1px solid hsl(var(--studio-border) / 0.4)', borderBottom: '1px solid hsl(var(--studio-border) / 0.4)' }}>
            {hasCash ? `ALL CASH · ${fmtMoney(account.cash)}` : 'NO POSITIONS — ACCOUNT UNFUNDED'}
          </div>
        ) : (
          <>
            <div style={{ ...GRID, padding: '0 4px 8px' }}>
              <span />
              <span style={HDR}>Position</span>
              <span style={{ ...HDR, textAlign: 'right' }}>Qty</span>
              <span style={{ ...HDR, textAlign: 'right' }}>Avg cost</span>
              <span style={{ ...HDR, textAlign: 'right' }}>Last</span>
              <span style={{ ...HDR, textAlign: 'right' }}>Mkt value</span>
              <span style={{ ...HDR, textAlign: 'right' }}>Unreal. P&amp;L</span>
            </div>
            {holdings.map((h) => <HoldingRow key={h.ticker} h={h} onOpen={tryOpen} />)}
          </>
        )}

        {/* setup lives where the empty state is: never-synced accounts get the
            full Agent Access section inline instead of a pointer to Settings. */}
        {!loading && !error && !account && !hasHoldings && (
          <div style={{ maxWidth: 640, marginTop: 8 }}>
            <div style={{ ...Mono, fontSize: 10, letterSpacing: '0.18em', color: 'hsl(var(--studio-accent))', textTransform: 'uppercase', marginBottom: 10 }}>Connect your agent</div>
            <AgentAccess />
          </div>
        )}
      </div>

      {/* ── zone 3 · decision log (header off the amber budget) ── */}
      <div style={{ marginTop: 32 }}>
        <div style={{ ...Mono, fontSize: 10, letterSpacing: '0.14em', color: 'hsl(var(--studio-text) / 0.7)', textTransform: 'uppercase' }}>Decision Log</div>
        <div style={{ ...Sans, fontSize: 12, color: 'hsl(var(--studio-text-subtle))', marginTop: 5 }}>Most recent first · agent trade rationale</div>
        <div style={{ marginTop: 10 }}>
          {loading ? null : error ? (
            <div style={cap}>—</div>
          ) : decisions.length === 0 ? (
            <div style={cap}>NO DECISIONS LOGGED</div>
          ) : (
            decisions.map((d, i) => <DecisionRow key={d.order_id || `${d.ticker}-${d.created_at}-${i}`} d={d} onOpen={tryOpen} />)
          )}
        </div>
      </div>

      {cfgOpen && <AgentConfigDialog cfg={agentCfg} syncedEquity={equity} onClose={() => setCfgOpen(false)} />}
    </div>
  )
}
